import { useState } from "react";
import { FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import classes from "./CreateInvoiceModal.module.css";

const CreateInvoiceModal = ({ isOpen, onClose, onSave, patientData }) => {
    const [invoiceData, setInvoiceData] = useState({
        patient: patientData || {
            name: "John Doe",
            id: "QPO - 12345",
            age: "34 Y / Male"
        },
        invoiceDate: "01 May 2024",
        dueDate: "04 Jun 2024",
        paymentMethod: "",
        visitEncounter: "",
        items: [
            {
                id: 1,
                service: "Consultation Fee",
                qty: 5,
                unitPrice: 50.00,
                discount: 0,
                tax: 0,
                amount: 250.00
            },
            {
                id: 2,
                service: "Complete Blood Count (CBC)",
                qty: 1,
                unitPrice: 45.00,
                discount: 0,
                tax: 0,
                amount: 45.00
            },
            {
                id: 3,
                service: "X-Ray Chest",
                qty: 1,
                unitPrice: 20.00,
                discount: 0,
                tax: 0,
                amount: 20.00
            }
        ],
        notes: ""
    });

    const [newItem, setNewItem] = useState({
        service: "",
        qty: 1,
        unitPrice: 0,
        discount: 0,
        tax: 0
    });

    const [nextItemId, setNextItemId] = useState(4);

    const handlePatientChange = (e) => {
        setInvoiceData({
            ...invoiceData,
            patient: {
                ...invoiceData.patient,
                name: e.target.value
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInvoiceData({
            ...invoiceData,
            [name]: value
        });
    };

    const handleItemChange = (id, field, value) => {
        const updatedItems = invoiceData.items.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value };

                // Recalculate amount if qty, unitPrice, discount, or tax changes
                if (['qty', 'unitPrice', 'discount', 'tax'].includes(field)) {
                    const qty = field === 'qty' ? parseFloat(value) || 0 : item.qty;
                    const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : item.unitPrice;
                    const discount = field === 'discount' ? parseFloat(value) || 0 : item.discount;
                    const tax = field === 'tax' ? parseFloat(value) || 0 : item.tax;

                    updatedItem.amount = (qty * unitPrice) - (qty * unitPrice * discount / 100) + (qty * unitPrice * tax / 100);
                    updatedItem.amount = parseFloat(updatedItem.amount.toFixed(2));
                }

                return updatedItem;
            }
            return item;
        });

        setInvoiceData({
            ...invoiceData,
            items: updatedItems
        });
    };

    const handleAddItem = () => {
        if (!newItem.service) {
            alert("Please enter a service name");
            return;
        }

        const qty = parseFloat(newItem.qty) || 0;
        const unitPrice = parseFloat(newItem.unitPrice) || 0;
        const discount = parseFloat(newItem.discount) || 0;
        const tax = parseFloat(newItem.tax) || 0;
        const amount = (qty * unitPrice) - (qty * unitPrice * discount / 100) + (qty * unitPrice * tax / 100);

        const newItemObj = {
            id: nextItemId,
            service: newItem.service,
            qty: qty,
            unitPrice: unitPrice,
            discount: discount,
            tax: tax,
            amount: parseFloat(amount.toFixed(2))
        };

        setInvoiceData({
            ...invoiceData,
            items: [...invoiceData.items, newItemObj]
        });

        setNewItem({
            service: "",
            qty: 1,
            unitPrice: 0,
            discount: 0,
            tax: 0
        });
        setNextItemId(nextItemId + 1);
    };

    const handleRemoveItem = (id) => {
        setInvoiceData({
            ...invoiceData,
            items: invoiceData.items.filter(item => item.id !== id)
        });
    };

    const handleNewItemChange = (e) => {
        const { name, value } = e.target;
        setNewItem({
            ...newItem,
            [name]: value
        });
    };

    const calculateTotals = () => {
        const subtotal = invoiceData.items.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
        const discount = invoiceData.items.reduce((sum, item) => sum + (item.qty * item.unitPrice * item.discount / 100), 0);
        const tax = invoiceData.items.reduce((sum, item) => sum + (item.qty * item.unitPrice * item.tax / 100), 0);
        const total = invoiceData.items.reduce((sum, item) => sum + item.amount, 0);

        return {
            subtotal: parseFloat(subtotal.toFixed(2)),
            discount: parseFloat(discount.toFixed(2)),
            tax: parseFloat(tax.toFixed(2)),
            total: parseFloat(total.toFixed(2))
        };
    };

    const totals = calculateTotals();

    const handleSave = (status) => {
        const invoiceDataToSave = {
            ...invoiceData,
            status: status,
            totals: totals,
            createdDate: new Date().toISOString()
        };
        onSave(invoiceDataToSave);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={classes.modalOverlay} onClick={onClose}>
            <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                <div className={classes.modalHeader}>
                    <h2>Create Invoice</h2>
                    <p>Create a new invoice for patient services</p>
                    <button className={classes.closeButton} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className={classes.modalContent}>
                    {/* Patient Details Section */}
                    <div className={classes.section}>
                        <h3>Patient Details</h3>
                        <div className={classes.formRow}>
                            <div className={classes.formGroup}>
                                <label>Patient</label>
                                <p className={classes.fieldLabel}>Patient by name or ID.</p>
                                <input
                                    type="text"
                                    value={invoiceData.patient.name}
                                    onChange={handlePatientChange}
                                    className={classes.input}
                                />
                                <div className={classes.patientId}>
                                    <span>{invoiceData.patient.id}</span>
                                    <span className={classes.divider}>|</span>
                                    <span>{invoiceData.patient.age}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Details Section */}
                    <div className={classes.section}>
                        <h3>Invoice Details</h3>
                        <div className={classes.formRow}>
                            <div className={classes.formGroup}>
                                <label>Invoice Date</label>
                                <input
                                    type="text"
                                    name="invoiceDate"
                                    value={invoiceData.invoiceDate}
                                    onChange={handleInputChange}
                                    className={classes.input}
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <label>Due Date</label>
                                <input
                                    type="text"
                                    name="dueDate"
                                    value={invoiceData.dueDate}
                                    onChange={handleInputChange}
                                    className={classes.input}
                                />
                                <small className={classes.helperText}>(14 Days)</small>
                            </div>
                            <div className={classes.formGroup}>
                                <label>Payment Method</label>
                                <select
                                    name="paymentMethod"
                                    value={invoiceData.paymentMethod}
                                    onChange={handleInputChange}
                                    className={classes.select}
                                >
                                    <option value="">Select payment method</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="Insurance">Insurance</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </select>
                            </div>
                        </div>
                        <div className={classes.formRow}>
                            <div className={classes.formGroup}>
                                <label>Select visit / encounter</label>
                                <select
                                    name="visitEncounter"
                                    value={invoiceData.visitEncounter}
                                    onChange={handleInputChange}
                                    className={classes.select}
                                >
                                    <option value="">Select visit / encounter</option>
                                    <option value="OPD-12345">OPD-12345 - 01 May 2024</option>
                                    <option value="OPD-12346">OPD-12346 - 30 Apr 2024</option>
                                    <option value="IPD-54321">IPD-54321 - 28 Apr 2024</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Items Section */}
                    <div className={classes.section}>
                        <h3>Invoice Items</h3>
                        <div className={classes.tableContainer}>
                            <table className={classes.itemsTable}>
                                <thead>
                                    <tr>
                                        <th>Item / Service</th>
                                        <th>Qty</th>
                                        <th>Unit Price</th>
                                        <th>Discount</th>
                                        <th>Tax</th>
                                        <th>Amount</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceData.items.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={item.service}
                                                    onChange={(e) => handleItemChange(item.id, 'service', e.target.value)}
                                                    className={classes.tableInput}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.qty}
                                                    onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}
                                                    className={classes.tableInputSmall}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.unitPrice}
                                                    onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}
                                                    className={classes.tableInputSmall}
                                                    step="0.01"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.discount}
                                                    onChange={(e) => handleItemChange(item.id, 'discount', e.target.value)}
                                                    className={classes.tableInputSmall}
                                                    step="0.01"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.tax}
                                                    onChange={(e) => handleItemChange(item.id, 'tax', e.target.value)}
                                                    className={classes.tableInputSmall}
                                                    step="0.01"
                                                />
                                            </td>
                                            <td className={classes.amountCell}>
                                                ${item.amount.toFixed(2)}
                                            </td>
                                            <td>
                                                <button
                                                    className={classes.removeButton}
                                                    onClick={() => handleRemoveItem(item.id)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className={classes.addItemRow}>
                                        <td>
                                            <input
                                                type="text"
                                                name="service"
                                                placeholder="Service name"
                                                value={newItem.service}
                                                onChange={handleNewItemChange}
                                                className={classes.tableInput}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                name="qty"
                                                value={newItem.qty}
                                                onChange={handleNewItemChange}
                                                className={classes.tableInputSmall}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                name="unitPrice"
                                                value={newItem.unitPrice}
                                                onChange={handleNewItemChange}
                                                className={classes.tableInputSmall}
                                                step="0.01"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                name="discount"
                                                value={newItem.discount}
                                                onChange={handleNewItemChange}
                                                className={classes.tableInputSmall}
                                                step="0.01"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                name="tax"
                                                value={newItem.tax}
                                                onChange={handleNewItemChange}
                                                className={classes.tableInputSmall}
                                                step="0.01"
                                            />
                                        </td>
                                        <td colSpan="2">
                                            <button
                                                className={classes.addItemButton}
                                                onClick={handleAddItem}
                                            >
                                                <FaPlus /> Add Item
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Notes Section */}
                    <div className={classes.section}>
                        <h3>Notes (Optional)</h3>
                        <textarea
                            name="notes"
                            value={invoiceData.notes}
                            onChange={handleInputChange}
                            className={classes.textarea}
                            placeholder="Add additional notes or instructions."
                            rows="3"
                        />
                    </div>

                    {/* Invoice Summary */}
                    <div className={classes.summarySection}>
                        <h3>Invoice Summary</h3>
                        <div className={classes.summaryRow}>
                            <span>Subtotal:</span>
                            <span>${totals.subtotal.toFixed(2)}</span>
                        </div>
                        <div className={classes.summaryRow}>
                            <span>Discount:</span>
                            <span>${totals.discount.toFixed(2)}</span>
                        </div>
                        <div className={classes.summaryRow}>
                            <span>Tax (3%):</span>
                            <span>${totals.tax.toFixed(2)}</span>
                        </div>
                        <div className={`${classes.summaryRow} ${classes.totalRow}`}>
                            <span>Total Amount:</span>
                            <span>${totals.total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className={classes.modalFooter}>
                    <button className={classes.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button className={classes.draftButton} onClick={() => handleSave('Draft')}>
                        Save as Draft
                    </button>
                    <button className={classes.createButton} onClick={() => handleSave('Created')}>
                        Create Invoice
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateInvoiceModal;